import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import Button from './Button'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children?: React.ReactNode
  footer?: React.ReactNode
  size?: string
  closeOnBackdrop?: boolean
  /**
   * Ersetzt die Standard-Titelzeile komplett. Fuer Fullscreen-Ansichten, die eine
   * eigene gepinnte Kopfleiste mitbringen (Buchhaltung-Detail).
   */
  headerContent?: React.ReactNode
  /**
   * Ersetzt die .modal-body-Klassen. Split-Views brauchen ein Grid ohne Padding
   * und ohne overflow-y-auto, weil dort jede Spalte fuer sich scrollt.
   */
  bodyClassName?: string
  /** Zusatzklassen fuer die Modal-Box selbst. */
  className?: string
}

const Modal = ({
  isOpen, onClose, title, children, footer, size = 'md', closeOnBackdrop = true,
  headerContent, bodyClassName, className = '',
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) onClose() }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) { document.documentElement.style.overflow = 'hidden'; document.body.style.overflow = 'hidden' }
    else { document.documentElement.style.overflow = ''; document.body.style.overflow = '' }
    return () => { document.documentElement.style.overflow = ''; document.body.style.overflow = '' }
  }, [isOpen])

  if (!isOpen) return null

  const sizes: Record<string, string> = {
    sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl', '2xl': 'max-w-5xl',
    full: 'max-w-[90vw]',
    // Tailwind-Utilities liegen hinter den @layer-components-Klassen, gewinnen also
    // gegen die Vorgaben von .modal (max-h-[90vh], mx-4, rounded-xl).
    screen: 'max-w-none w-screen h-screen max-h-none mx-0 rounded-none',
  }

  return (
    <div className="modal-backdrop animate-fadeIn" onClick={(e) => { if (closeOnBackdrop && e.target === e.currentTarget) onClose() }} onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
      <div ref={modalRef} className={`modal ${sizes[size] || sizes.md} animate-fadeIn w-full overflow-hidden ${className}`} role="dialog" aria-modal="true">
        {headerContent ?? (
          <div className="modal-header">
            <h2 className="mr-2 min-w-0 overflow-hidden break-words text-lg font-semibold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600"><X className="h-5 w-5" /></button>
          </div>
        )}
        <div className={bodyClassName ?? 'modal-body'}>{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  )
}

interface ConfirmProps {
  isOpen: boolean; onClose: () => void; onConfirm: () => void
  title?: string; message?: string; confirmText?: string; cancelText?: string; variant?: string; loading?: boolean
}

Modal.Confirm = ({ isOpen, onClose, onConfirm, title = 'Bestaetigung', message, confirmText = 'Bestaetigen', cancelText = 'Abbrechen', variant = 'danger', loading = false }: ConfirmProps) => (
  <Modal isOpen={isOpen} onClose={onClose} title={title} footer={<><Button variant="outline" onClick={onClose} disabled={loading}>{cancelText}</Button><Button variant={variant} onClick={onConfirm} loading={loading}>{confirmText}</Button></>}>
    <p className="text-gray-600">{message}</p>
  </Modal>
)

export default Modal
